import { useEffect, useState } from 'react';

import type { ProvedorDeIa } from '@/data/brain';
import logoDoBrain from '@assets/brain/logo-brain.png';
import iconeDoGrok from '@assets/brain/grok.png';

/**
 * Marcas do Brain original: o logo dele e os dos provedores de IA no seletor de
 * modelo. Claude e Gemini vêm do mesmo pacote de ícones que o original usava
 * (`@lobehub/icons`, pelo CDN, versão fixa); Grok é o PNG local do original; o
 * logo da OpenAI é o traço oficial que o original desenhava. Parte da exceção
 * visual do Brain — ver CLAUDE.md.
 */

const LOBE = 'https://unpkg.com/@lobehub/icons-static-svg@1.95.0/icons/';

/** Traço oficial do logo da OpenAI, o mesmo `viewBox` do original. */
const TRACO_DA_OPENAI =
    'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1195 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4533l-.142.0805L8.704 5.4596a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l3.854-2.2092 3.8539 2.2092v4.4184l-3.8539 2.2186-3.854-2.2186z';

export function LogoDoBrain({ tamanho = 42 }: { tamanho?: number }) {
    return <img src={logoDoBrain} alt="" width={tamanho} height={tamanho} style={{ objectFit: 'contain', flexShrink: 0 }} />;
}

/** Imagem do CDN com recaída: sem rede, fica a inicial em vez de uma imagem quebrada. */
function MarcaRemota({ arquivo, nome, tamanho }: { arquivo: string; nome: string; tamanho: number }) {
    const [falhou, setFalhou] = useState(false);
    useEffect(() => setFalhou(false), [arquivo]);
    if (falhou) {
        return (
            <span aria-hidden="true" style={{ width: tamanho, height: tamanho, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.max(8, Math.round(tamanho * 0.62)), fontWeight: 700, flexShrink: 0 }}>
                {nome.charAt(0)}
            </span>
        );
    }
    return (
        <img
            src={`${LOBE}${arquivo}`} alt="" width={tamanho} height={tamanho} loading="lazy" decoding="async"
            onError={() => setFalhou(true)}
            style={{ display: 'block', flexShrink: 0, objectFit: 'contain' }}
        />
    );
}

export interface IconeDoProvedorProps {
    /** `automatico` é o Auto/Rápido do Brain, com o logo dele. */
    provedor: ProvedorDeIa | 'automatico';
    tamanho?: number;
}

export function IconeDoProvedor({ provedor, tamanho = 16 }: IconeDoProvedorProps) {
    switch (provedor) {
        case 'anthropic':
            return <MarcaRemota arquivo="claude-color.svg" nome="Claude" tamanho={tamanho} />;
        case 'gemini':
            return <MarcaRemota arquivo="gemini-color.svg" nome="Gemini" tamanho={tamanho} />;
        case 'openrouter':
            return <MarcaRemota arquivo="openrouter.svg" nome="OpenRouter" tamanho={tamanho} />;
        case 'grok':
            return (
                <img
                    src={iconeDoGrok} alt="" width={tamanho} height={tamanho} loading="lazy" decoding="async"
                    style={{ display: 'block', flexShrink: 0, objectFit: 'contain', borderRadius: Math.max(4, Math.round(tamanho * 0.18)) }}
                />
            );
        case 'openai':
            return (
                <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" style={{ display: 'block', flexShrink: 0 }}>
                    <path d={TRACO_DA_OPENAI} />
                </svg>
            );
        default:
            return <LogoDoBrain tamanho={tamanho} />;
    }
}
